import { useState } from 'react';
import { ShieldCheck, X } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { DatePicker } from '@/shared/components/ui/date-picker';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { cn } from '@/shared/lib/cn';

/**
 * Garantía por línea (2026-10-29), compartido por Nueva cotización y Editar
 * cotización. Regla en server/src/lib/garantias.ts: los meses salen del ABM
 * de Artículos; si el producto no tiene, se pide la fecha de vencimiento a
 * mano y queda marcada como manual.
 */

/** Campos de garantía de una línea en el editor. */
export type GarantiaLinea = {
  llevaGarantia: boolean;
  /** Meses del producto (null si no tiene duración cargada). */
  garantiaMeses: number | null;
  /** yyyy-mm-dd. En el editor: el vencimiento manual (producto sin duración).
   *  En las vistas de una venta despachada: el vencimiento calculado. */
  garantiaVence: string | null;
};

export const SIN_GARANTIA: GarantiaLinea = { llevaGarantia: false, garantiaMeses: null, garantiaVence: null };

/** Meses de garantía del artículo según el ABM (null = sin duración). */
export function mesesGarantiaDe(raw: string | null | undefined): number | null {
  const n = Number(String(raw ?? '').trim());
  return Number.isInteger(n) && n > 0 ? n : null;
}

/** Botón de la barra de agregar, como el de Regalo: se arma antes de agregar el artículo. */
export function GarantiaToggle({ armed, onToggle }: { armed: boolean; onToggle: () => void }) {
  const hint = armed ? 'El próximo artículo se agrega con garantía' : 'Agregar con garantía';
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="icon"
          aria-label={hint}
          aria-pressed={armed}
          onClick={onToggle}
          className={cn(
            'h-10 w-10',
            armed && 'border-foreground bg-foreground text-background hover:bg-foreground/90 hover:text-background',
          )}
        >
          <ShieldCheck className="h-4 w-4" aria-hidden />
        </Button>
      </TooltipTrigger>
      <TooltipContent>{hint}</TooltipContent>
    </Tooltip>
  );
}

export const ddmmyyyy = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;

/** Badge de la línea; con `onQuitar` muestra la × para sacarle la garantía. */
export function GarantiaBadge({ linea, onQuitar }: { linea: GarantiaLinea; onQuitar?: () => void }) {
  if (!linea.llevaGarantia) return null;
  const manual = linea.garantiaMeses == null;
  const texto = manual
    ? `Garantía hasta ${linea.garantiaVence ? ddmmyyyy(linea.garantiaVence) : '?'}`
    : `Garantía ${linea.garantiaMeses} meses${linea.garantiaVence ? ` · hasta ${ddmmyyyy(linea.garantiaVence)}` : ''}`;
  return (
    <span className="inline-flex items-center gap-1 rounded-md bg-sky-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-sky-700">
      <ShieldCheck className="h-3 w-3" aria-hidden /> {texto}
      {manual && <span className="font-normal normal-case">(manual)</span>}
      {onQuitar && (
        <button
          type="button"
          onClick={onQuitar}
          aria-label="Quitar garantía"
          className="ml-0.5 rounded hover:bg-sky-200"
        >
          <X className="h-3 w-3" aria-hidden />
        </button>
      )}
    </span>
  );
}

/** Mañana en yyyy-mm-dd (hora local): el vencimiento manual tiene que ser futuro. */
function mananaIso(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Pide el vencimiento cuando el producto no tiene duración de garantía en el ABM. */
export function GarantiaFechaDialog({
  articulo,
  onConfirm,
  onCancel,
}: {
  /** null = cerrado. */
  articulo: string | null;
  onConfirm: (venceIso: string) => void;
  onCancel: () => void;
}) {
  const [fecha, setFecha] = useState('');
  const min = mananaIso();
  return (
    <Dialog
      open={articulo != null}
      onOpenChange={(o) => {
        if (!o) {
          setFecha('');
          onCancel();
        }
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Vencimiento de la garantía</DialogTitle>
          <DialogDescription>
            "{articulo}" no tiene una duración de garantía cargada en el ABM de Artículos. Indicá
            hasta cuándo vale; queda registrada como fecha manual.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <DatePicker value={fecha} onChange={setFecha} min={min} className="h-12 w-full" ariaLabel="Vencimiento de la garantía" />
        </DialogBody>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setFecha('');
              onCancel();
            }}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            disabled={!fecha || fecha < min}
            onClick={() => {
              const f = fecha;
              setFecha('');
              onConfirm(f);
            }}
          >
            Agregar con garantía
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
